import CommonTitleBar from "components/common/CommonTitleBar";
import Filters from "components/common/Filters";
import NoDataFound from "components/common/NoDataFound";
import Paginations from "components/common/Paginations";
import Spinners from "components/common/Spinners";
import { getMinMaxDate, handleStopPropagation } from "components/common/Utils";
import {
  customerStatusOptions,
  orderByOptions,
  platformOptions,
  statusOptions,
} from "components/constants/StatusKeys";
import React, { useEffect, useState } from "react";
import {
  Badge,
  Col,
  Container,
  Dropdown,
  Modal,
  Row,
  Table,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  getAllCustomers,
  getPlatformVersions,
  updateCustomerStatus,
} from "store/actions/customerSlice";
import { showAlert } from "store/reducers/alertReducer";
import StartIcon from "assets/images/icons/star.svg";
import { Ellipsis } from "lucide-react";
import AddCashbackForCustomers from "./AddCashbackForCustomers";
import {
  customerGenderOptions,
  ratingOptions,
  userRoleType,
} from "components/constants/DropdownOptions";
import {
  resetFilterState,
  setSortedCustomers,
  updateFilterState,
} from "store/reducers/customerReducer";
import Arrows from "assets/images/icons/arrows.svg";
import TableShimmer from "components/ShimmerEffects/CommonShimmer/TableShimmer";
import Riyal from "assets/images/icons/riyal.svg";
import AddCustomer from "./AddCustomer";

const Customers = () => {
  const { t } = useTranslation();
  document.title = t("Customers | Ashjar Space");

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [selectedIds, setSelectedIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);

  const {
    loading,
    perPage,
    totalCount,
    totalPages,
    currentPage,
    customers,
    filterState,
    versions,
  } = useSelector((state) => state.Customers);

  const [show, setShow] = useState(false);
  const [show1, setShow1] = useState(false);
  const [modalType, setModalType] = useState(null);

  const [keyword, setKeyword] = useState(filterState.keyword || "");
  const [dates, setDates] = useState([]);
  const [selectedFilters, setSelectedFilters] = useState(
    filterState.selectedFilters || {
      status: -1,
      orderBy: -1,
      roleType: [],
      gender: [],
      rating: -1,
      revenueRangeMin: null,
      revenueRangeMax: null,
      platform: "",
      version: "",
    }
  );

  useEffect(() => {
    getAllData();
  }, []);

  const getAllData = (page, action) => {
    const { actionType } = action || {}; // SEARCH, RESET, RESET_KEYWORD
    const {
      orderBy,
      status,
      roleType, //work type
      gender,
      rating,
      revenueRangeMin,
      revenueRangeMax,
      platform,
      version,
    } = selectedFilters;

    const pageToUse = page || filterState.currentPage || 1;

    // Check if both are filled and max is not greater than min
    if (
      revenueRangeMax != null && // Only proceed if max is not null
      revenueRangeMin != null && // And min is not null
      Number(revenueRangeMax) <= Number(revenueRangeMin)
    ) {
      dispatch(
        showAlert({
          text: t("Maximum Revenue should be greater than Minimum Revenue"),
          type: "error",
        })
      );
      return;
    }
    const isReset = actionType === "RESET";
    const payload = {
      perPage,
      pageNo: pageToUse,
      status: isReset ? -1 : status,
      orderBy: isReset ? -1 : orderBy,
      roleType:
        isReset ||
        (Array.isArray(roleType) && roleType.length === userRoleType.length)
          ? []
          : roleType,
      gender:
        isReset ||
        (Array.isArray(gender) &&
          gender.length === customerGenderOptions.length - 1)
          ? []
          : gender,

      rating: isReset ? -1 : rating,
      revenueRangeMin: isReset ? null : revenueRangeMin,
      revenueRangeMax: isReset ? null : revenueRangeMax,
      dateRange: isReset ? {} : getMinMaxDate(dates),
      platform: isReset ? "" : platform === "all" ? "" : platform,
      version: isReset ? "" : platform === "all" ? "" : version,
      keyword: ["RESET", "RESET_KEYWORD"].includes(actionType)
        ? ""
        : keyword || "",
    };
    dispatch(getAllCustomers(payload));

    dispatch(
      updateFilterState({
        currentPage: pageToUse,
        keyword,
        dates,
        selectedFilters,
      })
    );
  };

  const handleReset = () => {
    setKeyword("");
    setDates([]);
    setSelectedIds([]);
    setSelectAll(false);
    setSelectedFilters({
      orderBy: -1,
      status: -1,
      roleType: [],
      gender: [],
      rating: -1,
      revenueRangeMin: null,
      revenueRangeMax: null,
      platform: "all",
      version: "",
    });
    dispatch(resetFilterState());
    // getAllData(filterState.pageNumber, { actionType: "RESET" });
    const payload = {
      perPage,
      pageNo: 1,
      status: -1,
      orderBy: -1,
      roleType: [],
      gender: [],
      rating: -1,
      revenueRangeMin: null,
      revenueRangeMax: null,
      dateRange: {},
      keyword: "",
    };
    dispatch(getAllCustomers(payload));
  };

  const filters = [
    {
      key: "filterBy",
      placeholder: "Filter By",
      type: "advancedFilter",
      filters: [
        {
          name: "Gender",
          key: "gender",
          type: "checkbox",
          options: customerGenderOptions,
        },
        {
          name: "Work Type",
          key: "roleType",
          type: "checkbox",
          options: [{ name: "All", value: null }, ...userRoleType],
        },
        {
          name: "Revenue Range",
          key: "revenueRange",
          type: "range",
          min: 0,
          max: 1000,
          step: 100,
          defaultMin: 0,
          defaultMax: 1000,
        },
        {
          name: "Rating",
          key: "rating",
          type: "buttons",
          options: ratingOptions,
        },
      ],
    },
    {
      key: "status",
      placeholder: "Status",
      options: customerStatusOptions,
      type: "singleSelect",
    },
    {
      key: "platform",
      placeholder: "Platform",
      options: platformOptions,
      type: "singleSelect",
    },
    ...(["android", "iOS"].includes(selectedFilters?.platform)
      ? [
          {
            key: "version",
            placeholder: "Version",
            options: versions,
            type: "singleSelect",
          },
        ]
      : []),
    {
      key: "orderBy",
      placeholder: "Order By",
      options: orderByOptions,
      type: "singleSelect",
    },
  ];

  const handleSelectAll = () => {
    const newValue = !selectAll;
    setSelectAll(newValue);
    setSelectedIds(newValue ? customers.map((item) => item._id) : []);
  };

  const handleCheckboxChange = (id) => {
    let updatedSelected = [];

    if (selectedIds.includes(id)) {
      updatedSelected = selectedIds.filter((itemId) => itemId !== id);
    } else {
      updatedSelected = [...selectedIds, id];
    }

    setSelectedIds(updatedSelected);
    setSelectAll(updatedSelected.length === customers.length);
  };

  const [sortField, setSortField] = useState("");
  const [sortDirection, setSortDirection] = useState("asc");

  // Create a single, reusable sorting function
  const handleSort = (field) => {
    // Toggle direction if clicking the same field
    const newDirection =
      sortField === field && sortDirection === "asc" ? "desc" : "asc";

    setSortField(field);
    setSortDirection(newDirection);

    // Create a sorted copy of the customers array
    const sortedCustomers = [...customers].sort((a, b) => {
      // Handle null values by treating them as 0
      const valueA = a[field] ?? 0;
      const valueB = b[field] ?? 0;

      return newDirection === "asc" ? valueA - valueB : valueB - valueA;
    });

    dispatch(setSortedCustomers(sortedCustomers));
  };

  // Create a reusable column header component
  const SortableHeader = ({ field, label }) => (
    <div className="c_p" onClick={() => handleSort(field)}>
      <img src={Arrows} width={16} height={16} alt="arrow" />
      {t(label)}
    </div>
  );

  useEffect(() => {
    const { platform } = selectedFilters || {};
    const payload = { platform: platform || "" };
    platform && platform !== "all" && dispatch(getPlatformVersions(payload));
  }, [selectedFilters?.platform]);

  const handleCustomerStatusUpdate = (id) => {
    const payload = {
      id,
      status: "1", // 1 for restore
    };

    dispatch(updateCustomerStatus(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
          getAllData();
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  const handleShow = () => {
    setShow1(true);
  };

  return (
    <React.Fragment>
      <div className="page-content common_page">
        <Container fluid>
          <CommonTitleBar
            title={t("Customers")}
            isCreateBtn={true}
            createBtnText={t("Add New")}
            onCreateBtnClick={() => {
              setModalType("ADD");
              handleShow();
            }}
          />
          <div className="common_wrapper">
            <Filters
              keyword={keyword}
              setKeyword={setKeyword}
              selectedFilters={selectedFilters}
              setSelectedFilters={setSelectedFilters}
              dates={dates}
              setDates={setDates}
              filters={filters}
              onSearch={getAllData}
              onReset={handleReset}
              isBtn={selectedIds?.length > 0 ? true : false}
              btnText={t("Add Cashback")}
              onBtnClick={() => setShow(true)}
              className="filter_2" // white bg
            />
            <Row>
              <Col lg={12}>
                {loading ? (
                  <TableShimmer col={9} />
                ) : customers.length === 0 ? (
                  <NoDataFound text={t("No customers found")} />
                ) : (
                  <div
                    className="common_table_style"
                    style={{ minHeight: showDropdown ? 210 : "unset" }}
                  >
                    <Table>
                      <thead className="t_head">
                        <tr>
                          <th>
                            <input
                              type="checkbox"
                              checked={selectAll}
                              onChange={handleSelectAll}
                            />
                          </th>
                          <th>{t("ID")}</th>
                          <th>{t("User")}</th>
                          <th>{t("OS Version")}</th>
                          <th>{t("Number")}</th>
                          <th>
                            <SortableHeader
                              field="totalBookings"
                              label="Total Bookings"
                            />
                          </th>
                          <th>
                            <SortableHeader
                              field="totalRevenue"
                              label="Total Revenue"
                            />
                          </th>
                          <th>
                            <SortableHeader
                              field="totalCashback"
                              label="Total Cashback"
                            />
                          </th>
                          <th>{t("Invite Users")}</th>
                          <th>
                            <SortableHeader
                              field="avgRating"
                              label="Average Rating"
                            />
                          </th>
                          <th>{t("Status")}</th>
                          <th>{t("Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {customers?.map((item, index) => (
                          <tr key={item?._id}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedIds?.includes(item?._id)}
                                onChange={() => handleCheckboxChange(item?._id)}
                              />
                            </td>
                            <td>{(currentPage - 1) * perPage + index + 1}</td>
                            <td>
                              <p
                                onClick={() =>
                                  navigate(
                                    `/customers/customer-information?id=${item?._id}`
                                  )
                                }
                                className="c_p text-decoration-underline"
                              >
                                {item?.name || "-"}
                              </p>
                            </td>
                            <td>
                              {item?.deviceInfo?.platform === "android"
                                ? "Android"
                                : item?.deviceInfo?.platform || "-"}
                              {item?.deviceInfo?.appVersion &&
                                ` (${item?.deviceInfo?.appVersion})`}
                            </td>
                            <td>{item.phone || "-"}</td>
                            <td>{item?.totalBookings ?? 0}</td>
                            <td>
                              <img
                                src={Riyal}
                                width={15}
                                height={15}
                                alt="riyal"
                                className="me-1"
                              />
                              {item?.totalRevenue?.toFixed(2)}
                            </td>
                            <td>
                              <img
                                src={Riyal}
                                width={15}
                                height={15}
                                alt="riyal"
                                className="me-1"
                              />
                              {item?.totalCashback?.toFixed(2)}
                            </td>
                            <td>{item?.inviteUsers ?? 0}</td>
                            <td>
                              <div className="d-flex align-items-center gap-1">
                                <img
                                  src={StartIcon}
                                  alt=""
                                  width={15}
                                  height={15}
                                />
                                <p className="fs_14">{item?.avgRating ?? 0}</p>
                              </div>
                            </td>
                            <td>
                              <Badge
                                bg={
                                  item.status === 1
                                    ? " bg_primary"
                                    : item.status === 0
                                    ? "secondary"
                                    : "danger"
                                }
                              >
                                {item.status === 1
                                  ? t("Active")
                                  : item.status === 0
                                  ? t("Deactive")
                                  : t("Deleted")}
                              </Badge>
                            </td>
                            <td>
                              <div className="d-flex align-items-center gap-3">
                                <Dropdown
                                  className="custom_dropdown list_action_dropdown"
                                  drop={"down"}
                                  onClick={handleStopPropagation}
                                  onToggle={(isOpen) => setShowDropdown(isOpen)}
                                >
                                  <Dropdown.Toggle>
                                    <Ellipsis className="text_black" />
                                  </Dropdown.Toggle>

                                  <Dropdown.Menu align="end">
                                    <Dropdown.Item
                                      href="#"
                                      onClick={() => {
                                        navigate(
                                          `/customers/customer-information?id=${item?._id}`
                                        );
                                      }}
                                    >
                                      {t("View")}
                                    </Dropdown.Item>
                                    {item?.status === 2 && (
                                      <Dropdown.Item
                                        href="#"
                                        onClick={() => {
                                          handleCustomerStatusUpdate(item?._id);
                                        }}
                                      >
                                        {t("Restore")}
                                      </Dropdown.Item>
                                    )}
                                  </Dropdown.Menu>
                                </Dropdown>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </div>
                )}
              </Col>
            </Row>
          </div>
          {!loading && (
            <Paginations
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={(page) => {
                dispatch(
                  updateFilterState({
                    ...filterState,
                    currentPage: page,
                  })
                );
                getAllData(page);
              }}
              totalCount={totalCount}
            />
          )}
        </Container>
      </div>
      <Modal
        centered
        show={show}
        onHide={() => setShow(false)}
        className="common_modal customer_add_cashback"
      >
        <AddCashbackForCustomers
          selectedIds={selectedIds}
          setSelectedIds={setSelectedIds}
          setShow={setShow}
          getAllData={getAllData}
        />
      </Modal>
      <Modal
        centered
        show={show1}
        onHide={() => setShow1(false)}
        className="common_modal customer_add_cashback"
      >
        <AddCustomer setShow1={setShow1} modalType={modalType} />
      </Modal>
    </React.Fragment>
  );
};

export default Customers;
